import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { songsApi } from '@/services/api/songs';
import { commentsApi } from '@/services/api/comments';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CoverArt from '@/components/common/CoverArt';
import { formatDate, formatTimeAgo } from '@/utils/formatters';
import { ArrowLeft, Play, MessageSquare } from 'lucide-react';

export default function RevisionDetailsPage() {
  const { songId, revisionId } = useParams<{ songId: string; revisionId: string }>();
  const [commentText, setCommentText] = useState('');
  const { playSong } = useAudioPlayer();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['songDetails', songId],
    queryFn: () => songsApi.getSongDetails(Number(songId)),
    enabled: !!songId,
  });

  const commentMutation = useMutation({
    mutationFn: (text: string) =>
      commentsApi.createComment(Number(songId), {
        commentText: text,
        songRevisionId: Number(revisionId),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songDetails', songId] });
      setCommentText('');
    },
  });

  async function handleSubmitComment(e: React.FormEvent) {
    e.preventDefault();
    if (!commentText.trim()) return;
    commentMutation.mutate(commentText);
  }

  if (isLoading) return <LoadingSpinner />;
  if (!data || !data.song) return <div className="text-center text-white p-8">Song not found</div>;

  const { song, revisions = [], comments = [] } = data;
  const revision = revisions.find(r => r.songRevisionId === Number(revisionId));

  if (!revision) {
    return <div className="text-center text-white p-8">Revision not found</div>;
  }

  // Filter comments to only show those for this specific revision
  // Sort by done status (not done first) then by date (newest first)
  const revisionComments = comments
    .filter(comment => comment.songRevisionId === Number(revisionId))
    .sort((a, b) => {
      // Sort by done status first (false/undefined before true)
      if (a.done !== b.done) {
        return (a.done ? 1 : 0) - (b.done ? 1 : 0);
      }
      // Then by date (newest first)
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to={`/songs/${songId}`}
            className="flex items-center gap-2 text-text-secondary hover:text-white"
          >
            <ArrowLeft size={20} />
            Back to {song.songName}
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              {/* Row 1: cover art + revision label + title */}
              <div className="flex items-center gap-6">
                <CoverArt
                  songId={song.songId}
                  revisionId={revision.songRevisionId}
                  coverArtUrl={revision.coverArtUrl}
                  alt={revision.songName}
                  className="w-48 h-48 object-cover rounded"
                />
                <div className="flex-1">
                  <div className="mb-2">
                    <span className="text-sm text-text-muted">Revision of</span>
                    <h2 className="text-lg text-text-secondary">{song.songName}</h2>
                  </div>
                  <h1 className="text-3xl font-bold text-white">{revision.songName}</h1>
                </div>
              </div>

              {/* Row 2: play button + revised date */}
              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={() => playSong(song, revision)}
                  className="btn-secondary flex items-center gap-1.5 text-sm py-1.5 px-3"
                >
                  <Play size={14} />
                  Play
                </button>
                <p className="text-text-secondary">
                  Revised: {formatDate(revision.revised)}
                </p>
              </div>
            </div>
          </div>

          {/* Comments Sidebar */}
          <div>
            <div className="sticky top-4">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <MessageSquare size={20} />
                Comments ({revisionComments.length})
              </h2>

              {/* Add Comment Form */}
              <form onSubmit={handleSubmitComment} className="mb-6">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment about this revision..."
                  className="input resize-none h-24 mb-2"
                />
                <button
                  type="submit"
                  disabled={!commentText.trim() || commentMutation.isPending}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {commentMutation.isPending ? 'Posting...' : 'Post Comment'}
                </button>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {revisionComments.length > 0 ? (
                  revisionComments.map((comment) => (
                    <div
                      key={comment.commentId}
                      className={`card ${comment.done ? 'opacity-50' : ''}`}
                    >
                      <p className={`text-white text-sm mb-2 ${comment.done ? 'line-through text-text-muted' : ''}`}>
                        {comment.commentText}
                      </p>
                      <p className="text-text-muted text-xs">
                        {formatTimeAgo(comment.created)}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-text-secondary text-sm">No notes yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
